import type * as THREE from 'three';

/**
 * Typed publish/subscribe.
 *
 * The event map is a declared interface, so `emit('time:paused', { pased: true })` is a compile
 * error rather than a silently dropped event. There is no string-keyed `any` anywhere here.
 *
 * Hot-path note: `emit` does not allocate, but the *payload* is the caller's. Anything emitted
 * from inside a tick must pass a pre-allocated, module-scope payload object and treat it as
 * valid only for the duration of the call (SPEC §4.3, zero allocation in the tick).
 */

export type EventHandler<T> = (payload: T) => void;

/** Unsubscribes the handler it came from. Safe to call during dispatch. */
export type Unsubscribe = () => void;

/**
 * Internal handler slot type. Handlers for different keys have different payload types, so the
 * per-key arrays are stored under a single erased signature and narrowed at the public boundary
 * — the two casts in this file are the only place that erasure exists.
 */
type ErasedHandler = EventHandler<never>;

export class EventBus<EventMap extends object> {
  private readonly handlers = new Map<keyof EventMap, Array<ErasedHandler | null>>();

  /** Dispatch depth; removals during dispatch are deferred so iteration stays stable. */
  private dispatchDepth = 0;
  private needsCompaction = false;

  /** Subscribe to an event. Returns an unsubscribe function. */
  on<K extends keyof EventMap>(event: K, handler: EventHandler<EventMap[K]>): Unsubscribe {
    let list = this.handlers.get(event);
    if (list === undefined) {
      list = [];
      this.handlers.set(event, list);
    }
    list.push(handler as ErasedHandler);
    let removed = false;
    return () => {
      if (removed) return;
      removed = true;
      this.off(event, handler);
    };
  }

  /** Subscribe for exactly one dispatch. */
  once<K extends keyof EventMap>(event: K, handler: EventHandler<EventMap[K]>): Unsubscribe {
    const unsubscribe = this.on(event, (payload) => {
      unsubscribe();
      handler(payload);
    });
    return unsubscribe;
  }

  /** Unsubscribe a specific handler. */
  off<K extends keyof EventMap>(event: K, handler: EventHandler<EventMap[K]>): void {
    const list = this.handlers.get(event);
    if (list === undefined) return;
    const index = list.indexOf(handler as ErasedHandler);
    if (index < 0) return;
    // Tombstone rather than splice: a handler may be unsubscribing itself mid-dispatch.
    list[index] = null;
    this.needsCompaction = true;
    if (this.dispatchDepth === 0) this.compact();
  }

  /** Dispatch to every current subscriber, in subscription order. Does not allocate. */
  emit<K extends keyof EventMap>(event: K, payload: EventMap[K]): void {
    const list = this.handlers.get(event);
    if (list === undefined) return;
    this.dispatchDepth += 1;
    // Length is snapshotted: a handler that subscribes during dispatch runs from the next emit,
    // never re-entrantly within this one. Removals tombstone, so indices stay valid.
    const count = list.length;
    for (let i = 0; i < count; i += 1) {
      const handler = list[i];
      if (handler !== null) (handler as EventHandler<EventMap[K]>)(payload);
    }
    this.dispatchDepth -= 1;
    if (this.dispatchDepth === 0 && this.needsCompaction) this.compact();
  }

  /** Number of live handlers for an event. */
  listenerCount<K extends keyof EventMap>(event: K): number {
    const list = this.handlers.get(event);
    if (list === undefined) return 0;
    let count = 0;
    for (let i = 0; i < list.length; i += 1) if (list[i] !== null) count += 1;
    return count;
  }

  /** Drop every subscription. */
  clear(): void {
    this.handlers.clear();
    this.needsCompaction = false;
  }

  private compact(): void {
    for (const [event, list] of this.handlers) {
      let write = 0;
      for (let read = 0; read < list.length; read += 1) {
        const handler = list[read];
        if (handler !== null) {
          list[write] = handler;
          write += 1;
        }
      }
      list.length = write;
      if (write === 0) this.handlers.delete(event);
    }
    this.needsCompaction = false;
  }
}

/**
 * The game-wide event map. Add events here as milestones land; keep payloads flat and cheap.
 * Events emitted from inside the fixed tick must use pooled payloads — see the note above.
 */
export interface GameEvents {
  /** Simulation was reset to tick 0 with this seed. */
  'sim:reset': { seed: number };
  /** Debug time controls changed. */
  'time:paused': { paused: boolean };
  'time:scale': { scale: number };
  /** One tick was executed by an explicit single-step request. */
  'time:stepped': { tick: number };
  /** Debug overlay visibility toggled. */
  'debug:visibility': { visible: boolean };
  /** Backing buffer resized (device pixels, after render-scale). */
  'render:resize': { widthPx: number; heightPx: number };
  /**
   * A site has committed to a shot (SPEC §3.5.2, LAUNCHING). M6 turns this into a missile.
   * Emitted from inside the tick, so the payload is pooled and valid only during dispatch.
   */
  'threat:launch': {
    siteId: number;
    designation: string;
    originM: THREE.Vector3;
    salvoIndex: number;
  };
  /**
   * A missile's proximity fuse fired (SPEC §3.6.4). `damage` is the fraction of airframe
   * integrity removed; a near miss carries zero. Pooled payload, valid only during dispatch.
   */
  'missile:detonated': MissileImpact;
  /** A missile self-destructed: timed out, ran out of speed, or hit the ground. */
  'missile:expired': MissileImpact;
  /** The aircraft struck a target — the terminal event of a run (SPEC §3.8.2). */
  'target:impact': TargetImpact;
  /** A mission radio call fired (SPEC §3.10.1). Subtitled by the HUD, voiced by M9. */
  'mission:radio': { speaker: string; text: string };
  /** An objective changed state (SPEC §3.10.1). */
  'mission:objective': { id: string; state: string; description: string };
  /** The mission ended, one way or the other. */
  'mission:outcome': { missionId: string; success: boolean; reason: string };
}

/** The aircraft struck a target (SPEC §3.8.2). Pooled payload, valid only during dispatch. */
export interface TargetImpact {
  designation: string;
  kind: string;
  grade: string;
  failure: string;
  effect: number;
  aimPointDistanceM: number;
  impactSpeedMs: number;
  diveAngleDeg: number;
  destroyed: boolean;
  positionM: THREE.Vector3;
  secondaryFireS: number;
}

export interface MissileImpact {
  designation: string;
  missDistanceM: number;
  damage: number;
  positionM: THREE.Vector3;
  destroyed: boolean;
}

/** The application-wide bus. Systems import this rather than threading it through constructors. */
export const bus = new EventBus<GameEvents>();
